import React from "react";
import { Alert, Spin, Tooltip, Tag, Popconfirm } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
// La tabla de conceptos es literalmente la del sobre-recibo de la prenómina, no una copia: las
// dos pantallas revisan y ajustan un recibo y tienen que verse iguales.
import TablaConceptosRecibo from "../../PreNomina/SobreRecibo/TablaConceptosRecibo";
// `Columna` sigue viva sólo para los conceptos informativos, que son una lista suelta y no una
// pareja percepción/deducción.
import CeldaEditable from "../../PreNomina/SobreRecibo/CeldaEditable";
// Qué se ve en el recibo y qué no ofrece el selector de conceptos libres. Compartido con la
// pestaña 1 para que las dos escondan exactamente lo mismo.
import { conceptoVisibleEnRecibo, idsNoOfrecibles } from "./conceptosDelRecibo";

const fmtMXN = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

const thBase = {
  padding: "6px 8px", fontSize: 11, fontWeight: 600, textAlign: "center",
  border: "1px solid #b8a000", whiteSpace: "nowrap", color: "#fff",
};
const TH = { ...thBase, background: "#c9a227" };
const TD = { border: "1px solid #e0e0e0", padding: "4px 6px", fontSize: 12 };
const TD_NUM = { ...TD, textAlign: "right", whiteSpace: "nowrap" };
const TD_CEN = { ...TD, textAlign: "center" };

const esManual = (v) => v !== null && v !== undefined;

/**
 * Una de las dos columnas del desglose. Se pintan por separado —percepciones a la izquierda,
 * deducciones a la derecha— igual que el sobre recibo de CONTPAQi, porque es como el usuario
 * de nómina está acostumbrado a revisarlo.
 *
 * Con `editable` las celdas de Importe (y las de Valor, sólo en percepciones) se vuelven las MISMAS
 * celdas del sobre-recibo de Prenómina: se editan al enfocarlas, guardan al salir del campo y se
 * marcan en azul cuando el número lo puso una persona. `permiteValor` no es cosmético: el motor
 * —y el endpoint de importes de la prenómina— sólo aceptan `valorManual` en percepciones.
 */
const Columna = ({
  titulo, conceptos, total, etiquetaTotal, resaltarKeys = [],
  editable, permiteValor, esExtra = () => false,
  onEditarManual, onLimpiarManual, onQuitarExtra,
}) => (
  <div style={{ flex: 1, minWidth: 340 }}>
    <table style={{ borderCollapse: "collapse", width: "100%" }}>
      <thead>
        <tr>
          <th style={{ ...TH, textAlign: "left" }} colSpan={3}>{titulo}</th>
        </tr>
        <tr>
          <th style={TH}>Concepto</th>
          <th style={{ ...TH, width: 60 }}>Valor</th>
          <th style={{ ...TH, width: 100 }}>Importe</th>
        </tr>
      </thead>
      <tbody>
        {conceptos.length === 0 ? (
          <tr>
            <td style={{ ...TD_CEN, color: "#999", fontStyle: "italic" }} colSpan={3}>
              Sin conceptos
            </td>
          </tr>
        ) : (
          conceptos.map((c, i) => {
            const resaltar = resaltarKeys.some((k) => (c.descripcion || "").includes(k));
            const fondo = resaltar ? "#e6f4ff" : i % 2 ? "#fafafa" : undefined;
            const manualImporte = esManual(c.importeTotalManual);
            const manualValor   = esManual(c.valorManual);
            const id = c.conceptoNominaId ? String(c.conceptoNominaId) : null;

            return (
              <tr key={`${c.conceptoNominaId || c.descripcion}-${i}`}>
                <td style={{ ...TD, background: fondo, fontWeight: resaltar ? 600 : 400 }}>
                  {c.numero != null ? `${c.numero}. ` : ""}{c.descripcion}
                  {/* Este renglón se va al CFDI de separación (régimen 13). Sólo aparece cuando el
                      recibo se parte en dos, o sea cuando hay pagos por separación que cobrar. */}
                  {c.division === "separacion" && (
                    <Tooltip title="Va en el CFDI de indemnización o separación (régimen 13), aparte del recibo ordinario.">
                      <Tag color="gold" style={{ marginLeft: 4, fontSize: 10, padding: "0 4px" }}>
                        CFDI sep.
                      </Tag>
                    </Tooltip>
                  )}
                  {(manualImporte || manualValor) && (
                    editable && id ? (
                      <Popconfirm
                        title="¿Quitar el importe capturado de este concepto? Vuelve a calcularse con su fórmula."
                        okText="Quitar"
                        cancelText="Cancelar"
                        onConfirm={() => onLimpiarManual(id)}
                      >
                        <Tag
                          color="blue"
                          style={{ marginLeft: 4, fontSize: 10, padding: "0 4px", cursor: "pointer" }}
                        >
                          M ✕
                        </Tag>
                      </Popconfirm>
                    ) : (
                      <Tag color="blue" style={{ marginLeft: 4, fontSize: 10, padding: "0 4px" }}>M</Tag>
                    )
                  )}
                  {/* La basurita sólo va en los conceptos que se agregaron DESDE ESTA PANTALLA.
                      `agregadoManual` también es true para uno que alguien haya agregado antes
                      desde el sobre-recibo en Prenómina, y ahí el botón no podría quitar nada:
                      ese concepto vive en la línea, no en la captura del finiquito. */}
                  {editable && id && esExtra(id) && (
                    <Popconfirm
                      title="¿Quitar este concepto del finiquito?"
                      okText="Quitar"
                      cancelText="Cancelar"
                      onConfirm={() => onQuitarExtra(id)}
                    >
                      <DeleteOutlined
                        style={{ marginLeft: 6, color: "#ff4d4f", cursor: "pointer" }}
                      />
                    </Popconfirm>
                  )}
                </td>
                <td style={{ ...TD_CEN, background: fondo }}>
                  {editable && permiteValor && id ? (
                    <CeldaEditable
                      key={`v-${id}-${c.valor}`}
                      valor={c.valor || 0}
                      esManual={manualValor}
                      ancho={54}
                      onGuardar={(nv) => onEditarManual(id, "valorManual", nv)}
                    />
                  ) : (
                    c.valor ? Number(c.valor).toFixed(2) : ""
                  )}
                </td>
                <td style={{ ...TD_NUM, background: fondo }}>
                  {editable && id ? (
                    <CeldaEditable
                      key={`i-${id}-${c.importe}`}
                      valor={c.importe || 0}
                      esManual={manualImporte}
                      ancho={88}
                      onGuardar={(nv) => onEditarManual(id, "importeTotalManual", nv)}
                    />
                  ) : (
                    fmtMXN(c.importe)
                  )}
                </td>
              </tr>
            );
          })
        )}
        <tr>
          <td style={{ ...TD, fontWeight: 700, background: "#f0f0f0" }} colSpan={2}>
            {etiquetaTotal}
          </td>
          <td style={{ ...TD_NUM, fontWeight: 700, background: "#f0f0f0" }}>{fmtMXN(total)}</td>
        </tr>
      </tbody>
    </table>
  </div>
);

/**
 * Pestaña 2 — el desglose completo tal como quedará en el recibo: TODOS los conceptos de la
 * línea, no sólo los del finiquito. Es importante que se vea así: el neto que firma el
 * trabajador incluye su IMSS, su ISR ordinario y cualquier préstamo pendiente del periodo.
 *
 * Mientras el finiquito no está aplicado los importes son EDITABLES, con la misma mecánica del
 * sobre-recibo: un finiquito se negocia en pesos, no en días. Cada edición vuelve a correr el motor
 * —para eso es un solo empleado— así que el ISR de separación, la exención y el neto que se ven son
 * siempre los que se van a aplicar. Nada se escribe hasta presionar Aplicar finiquito.
 */
const DesgloseFiniquito = ({
  preview, calculando, bloqueado, extras,
  empresaId, headersToken,
  onEditarManual, onLimpiarManual, onQuitarExtra, onAgregarExtra, onQuitarConceptoLey,
  onLimpiarTodos,
}) => {
  if (!preview) {
    return (
      <Spin spinning={calculando}>
        <Alert
          type="info"
          showIcon
          message="Todavía no hay cálculo. Presiona Calcular en la barra de arriba."
        />
      </Spin>
    );
  }

  const conceptos = preview.conceptosLinea || [];
  const ordenar = (a, b) => (a.numero ?? 0) - (b.numero ?? 0);
  const editable = !bloqueado;

  // ── Qué se puede quitar y qué pasa al quitarlo ──────────────────────────────
  //
  // Dos cosas distintas bajo la misma basurita:
  //  · un concepto LIBRE (agregado aquí o en la pestaña 1) se quita de verdad;
  //  · uno de los nueve del finiquito no se borra, se DESACTIVA — es lo mismo que desmarcar su
  //    casilla "Aplica" en la pestaña 1, así que se puede volver a poner desde ahí. Sólo los
  //    marcados `desactivable`: el resto son de ley y no se apagan.
  //
  // Todo lo demás (el ISR ordinario, el IMSS, un préstamo del empleado) NO lleva basurita, igual
  // que en el sobre-recibo: un concepto calculado no se borra, cuando mucho se le fuerza el 0.
  //
  // La decisión va por `extras` y por la `key` del concepto de ley, nunca por `agregadoManual`:
  // ese flag también es true para un concepto que se agregó desde el sobre-recibo de Prenómina, y
  // ése no es de este finiquito.
  const idsExtra = new Set((extras || []).map((e) => String(e.conceptoNominaId)));
  const esExtra = (id) => idsExtra.has(id);

  const keyDeLey = new Map(
    (preview.filas || [])
      .filter((f) => f.conceptoNominaId && f.desactivable && f.aplica)
      .map((f) => [String(f.conceptoNominaId), f.key])
  );

  const puedeQuitar = (c) => {
    const id = String(c.conceptoNominaId);
    return esExtra(id) || keyDeLey.has(id);
  };

  const quitar = (c) => {
    const id = String(c.conceptoNominaId);
    if (esExtra(id)) return onQuitarExtra(id);
    const key = keyDeLey.get(id);
    if (key && onQuitarConceptoLey) onQuitarConceptoLey(key);
  };

  const tituloQuitar = (c) => (
    esExtra(String(c.conceptoNominaId))
      ? "¿Quitar este concepto del finiquito?"
      : "¿Quitar este concepto del finiquito? Se desactiva, no se borra: puedes volver a "
        + "activarlo desde la pestaña 1 marcando su casilla Aplica."
  );

  // Un renglón en cero se oculta —son los conceptos del catálogo que no aplicaron—, pero NUNCA
  // uno que el usuario tocó: si se fuerza un importe a 0 el renglón tiene que seguir a la vista,
  // o no habría forma de quitarle el manual. El mismo predicado decide qué NO ofrece el selector
  // de alta (ver conceptosDelRecibo.js): lo que se oculta aquí tiene que poderse agregar allá.
  const visible = conceptoVisibleEnRecibo;

  // `conceptosLinea` trae el importe YA efectivo en `importe` (el backend aplicó el manual), pero
  // la tabla compartida habla el idioma de `LineaPrenomina.conceptos`, donde el campo se llama
  // `importeTotal`. Sin esta traducción la tabla lee `undefined` y pinta TODO en 0.00 —importes,
  // totales y neto— sin ningún error. Se traduce aquí, en la frontera, y no dentro del componente
  // compartido: el que tiene una forma distinta es el finiquito.
  const paraTabla = (c) => ({ ...c, importeTotal: c.importe });

  const percepciones = conceptos.filter((c) => c.tipo === 1 && !c.noSuma && visible(c)).sort(ordenar).map(paraTabla);
  const deducciones  = conceptos.filter((c) => c.tipo === 2 && !c.noSuma && visible(c)).sort(ordenar).map(paraTabla);
  // noSuma = conceptos informativos: se calculan y se imprimen pero no entran a ningún total.
  // Se muestran aparte para que nadie intente cuadrar el neto sumándolos.
  const informativos = conceptos.filter((c) => c.noSuma && visible(c)).sort(ordenar);

  const { totales = {} } = preview;
  const hayManuales = conceptos.some((c) => esManual(c.importeTotalManual) || esManual(c.valorManual));

  // ── Los dos comprobantes ───────────────────────────────────────────────────
  // Con pagos por separación el recibo se timbra en DOS CFDI: el SAT no permite que los conceptos
  // 022/023/025 y su ISR convivan en el mismo complemento que el sueldo. El backend ya marcó cada
  // renglón (`division`) con la misma regla que usará el timbrado; aquí sólo se suman para que se
  // vea, antes de aplicar, qué va en cada comprobante.
  const netoDe = (division) => {
    const suyos = conceptos.filter((c) => c.division === division && !c.noSuma);
    const suma = (tipo) => suyos
      .filter((c) => c.tipo === tipo)
      .reduce((acc, c) => acc + (c.importe || 0), 0);
    return suma(1) - suma(2);
  };
  const netoOrdinario  = preview.seDivideElCfdi ? netoDe("ordinario")  : 0;
  const netoSeparacion = preview.seDivideElCfdi ? netoDe("separacion") : 0;

  // Los conceptos del finiquito y los agregados, juntos, para la tabla de gravado/exento.
  const filasFiniquito = [
    ...(preview.filas || []).map((f) => ({ ...f, etiqueta: f.label })),
    ...(preview.filasExtra || []).map((f) => ({ ...f, etiqueta: f.descripcion })),
  ];

  return (
    <Spin spinning={calculando}>
      {editable && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Los importes se pueden editar aquí, como en el sobre-recibo"
          description={
            <div style={{ fontSize: 12 }}>
              <p style={{ marginBottom: 4 }}>
                Escribe sobre cualquier importe y sal del campo: se vuelve a correr el cálculo y con
                él el <b>ISR de separación, la exención y el neto</b>. Lo que quede en azul con la
                marca <b>M</b> es lo que capturaste, y es lo que se va a aplicar —la fórmula del
                concepto ya no se usa en ese renglón—. <b>Nada se guarda hasta que presiones
                Aplicar finiquito.</b>
              </p>
              <p style={{ marginBottom: 0, color: "#888" }}>
                El <b>Valor</b> (cantidad/días) sólo se edita en percepciones. Para agregar un
                concepto usa el último renglón de cada columna, y para quitar uno, su basurita.
                {hayManuales && onLimpiarTodos && (
                  <>
                    {" "}
                    <span
                      style={{ color: "#1677ff", cursor: "pointer", fontWeight: 600 }}
                      onClick={onLimpiarTodos}
                    >
                      Quitar todos los importes capturados
                    </span>
                  </>
                )}
              </p>
            </div>
          }
        />
      )}

      {/* La misma tabla del sobre-recibo de la prenómina, no una parecida: las dos pantallas
          hacen lo mismo y comparten el componente para no separarse nunca. */}
      <TablaConceptosRecibo
        percepciones={percepciones}
        deducciones={deducciones}
        soloLectura={!editable}
        empresaId={empresaId}
        headersToken={headersToken}
        onEditarImporte={onEditarManual}
        onLimpiarManual={onLimpiarManual}
        onAgregar={onAgregarExtra}
        // El selector de alta esconde lo que ya se VE en el recibo (se le edita el importe en su
        // celda), los nueve del finiquito y el ISR de finiquito (el backend los rechaza) y lo ya
        // agregado. Un concepto de la línea que quedó en CERO sí se ofrece: está oculto en esta
        // tabla, así que si tampoco se pudiera agregar quedaría inalcanzable. Ver
        // conceptosDelRecibo.js.
        excluidos={idsNoOfrecibles({
          filas: preview.filas,
          conceptosLinea: conceptos,
          extras,
          isrFiniquitoConceptoId: preview.isrFiniquitoConceptoId,
        })}
        puedeQuitar={puedeQuitar}
        onQuitar={quitar}
        tituloQuitar={tituloQuitar}
        // Con pagos por separación, qué renglón se va al segundo comprobante.
        extrasNombre={(c) => (c.division === "separacion" ? (
          <Tooltip title="Este concepto se timbra en el CFDI de separación (régimen 13), aparte del ordinario.">
            <Tag color="purple" style={{ marginLeft: 4, fontSize: 10, padding: "0 4px" }}>CFDI sep.</Tag>
          </Tooltip>
        ) : null)}
        mensajeVacio="Sin conceptos — presiona Calcular en la barra de arriba"
        nota={(
          <>
            <b>Valor</b> = cantidad (días). Sólo aplica a percepciones. Los campos en{" "}
            <span style={{ color: "#1677ff", fontWeight: 600 }}>azul (M)</span> son los que
            capturaste, y ganan sobre la fórmula del concepto.
          </>
        )}
      />

      {/* ── Neto ──────────────────────────────────────────────────────────── */}
      <div
        style={{
          marginTop: 14, padding: "10px 14px", background: "#1677ff", color: "white",
          borderRadius: 4, display: "flex", justifyContent: "space-between",
          alignItems: "center", fontSize: 15, fontWeight: 700,
        }}
      >
        <span>Neto a pagar</span>
        <span>{fmtMXN(totales.totalNeto)}</span>
      </div>

      {/* ── Cómo se va a timbrar ──────────────────────────────────────────── */}
      {preview.seDivideElCfdi && (
        <div
          style={{
            marginTop: 10, padding: 12, background: "#fffbe6",
            border: "1px solid #ffe58f", borderRadius: 4, fontSize: 12,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Este finiquito se timbra en <b>dos CFDI</b>
          </div>
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap", marginBottom: 6 }}>
            <div>
              <div style={{ color: "#888", fontSize: 11 }}>Recibo ordinario (régimen 02)</div>
              <div style={{ fontWeight: 600 }}>{fmtMXN(netoOrdinario)}</div>
            </div>
            <div>
              <div style={{ color: "#888", fontSize: 11 }}>
                Indemnización o separación (régimen 13)
              </div>
              <div style={{ fontWeight: 600 }}>{fmtMXN(netoSeparacion)}</div>
            </div>
          </div>
          <div style={{ color: "#7c5a00" }}>
            Los conceptos marcados <Tag color="gold" style={{ fontSize: 10, padding: "0 4px" }}>CFDI sep.</Tag>
            —prima de antigüedad, pagos por separación e indemnizaciones, con su ISR— van en un
            comprobante aparte con <b>tipo de régimen 13</b> y nómina extraordinaria. Lo pide la guía
            de llenado del SAT: un mismo complemento de nómina no puede mezclar conceptos ordinarios
            y de separación. El empleado recibe <b>dos recibos</b>, y la suma de los dos netos es el
            neto de arriba.
          </div>
        </div>
      )}

      {/* ── El ISR de finiquito, desarmado ────────────────────────────────── */}
      {preview.isrFiniquitoBase > 0 && (
        <div
          style={{
            marginTop: 12, padding: 12, background: "#fafafa",
            border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 12,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            ISR del finiquito{" "}
            <Tooltip
              title={
                "Art. 96 último párrafo LISR: los pagos por separación no se gravan con la " +
                "tarifa del periodo, sino con la tasa efectiva que corresponde al último " +
                "sueldo mensual ordinario del trabajador."
              }
            >
              <span style={{ color: "#1677ff", cursor: "help", fontWeight: 400 }}>
                (¿cómo se calcula?)
              </span>
            </Tooltip>
          </div>
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
            <div>
              <div style={{ color: "#888", fontSize: 11 }}>Base gravada de separación</div>
              <div style={{ fontWeight: 600 }}>{fmtMXN(preview.isrFiniquitoBase)}</div>
            </div>
            <div>
              <div style={{ color: "#888", fontSize: 11 }}>Tasa efectiva</div>
              <div style={{ fontWeight: 600 }}>
                {((preview.isrFiniquitoTasa || 0) * 100).toFixed(4)} %
              </div>
            </div>
            <div>
              <div style={{ color: "#888", fontSize: 11 }}>ISR retenido</div>
              <div style={{ fontWeight: 600 }}>{fmtMXN(preview.isrFiniquito)}</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Gravado y exento por concepto del finiquito ───────────────────── */}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
          Gravado y exento de los conceptos del finiquito
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...TH, textAlign: "left" }}>Concepto</th>
                <th style={TH}>Días</th>
                <th style={TH}>Importe</th>
                <th style={TH}>Gravado ISR</th>
                <th style={TH}>Exento ISR</th>
              </tr>
            </thead>
            <tbody>
              {filasFiniquito
                .filter((f) => f.aplica && f.importe)
                .map((f) => (
                  <tr key={f.key}>
                    <td style={TD}>
                      {f.etiqueta}
                      {f.esExtra && (
                        <Tag color="blue" style={{ marginLeft: 6, fontSize: 10, padding: "0 4px" }}>
                          agregado
                        </Tag>
                      )}
                    </td>
                    <td style={TD_CEN}>
                      {f.esExtra ? "—" : (f.diasCapturados || 0).toFixed(4)}
                    </td>
                    <td style={TD_NUM}>{fmtMXN(f.importe)}</td>
                    <td style={TD_NUM}>{fmtMXN(f.gravado)}</td>
                    <td style={TD_NUM}>{fmtMXN(f.exento)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {informativos.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            Informativos (no suman al neto)
          </div>
          <Columna
            titulo="Conceptos informativos"
            conceptos={informativos}
            total={0}
            etiquetaTotal="No suman"
            editable={editable}
            permiteValor={false}
            esExtra={esExtra}
            onEditarManual={onEditarManual}
            onLimpiarManual={onLimpiarManual}
            onQuitarExtra={onQuitarExtra}
          />
        </div>
      )}
    </Spin>
  );
};

export default DesgloseFiniquito;
